import React, { useState } from 'react';
import PropTypes from 'prop-types';

import { ANALYTICS_PAGE_NAMES } from 'dpl/constants/analytics';
import { isLessThanBreakpoint } from 'dpl/util/grid';
import ImagePlaceholder from 'dpl/components/MediaGrid/ImagePlaceholder';
import MediaGridCarousel from 'dpl/components/MediaGrid/MediaGridCarousel';
import SmartImage from 'dpl/components/SmartImage';
import ClickableDiv from 'dpl/common/components/ClickableDiv';
import ClampedMultiLineText from 'dpl/common/components/ClampedMultiLineText';
import ParentHealthTestingBanner from 'dpl/common/components/ParentHealthTestingBanner';
import SectionViewTracker from 'dpl/common/components/SectionViewTracker';

import { usePuppyData } from '../hooks/usePuppyData';
import Section from './Section';
import Badge from './Badge';

const IS_LT_MD = isLessThanBreakpoint('md');

const PLACEHOLDER_IMG_SIZE = IS_LT_MD ? '80px' : '100px';

function PuppyParentPhotos({ name, photos }) {
  const [openMediaCarousel, setOpenMediaCarousel] = useState(false);
  return (
    <>
      {photos.length === 0 ? (
        <ImagePlaceholder
          className="br3 bg-white"
          caption={`The breeder hasn’t added a photo of ${name} yet`}
          imgSize={PLACEHOLDER_IMG_SIZE}
        />
      ) : (
        <>
          <ClickableDiv
            className="relative"
            onClick={() => setOpenMediaCarousel(true)}
          >
            <div className="aspect-ratio aspect-ratio--6x4 relative z-0">
              <div className="aspect-ratio--object">
                <SmartImage
                  Tag="div"
                  className="w-100 h-100 cover bg-center br3"
                  src={photos[0].url}
                  loadingClass="bg-light-gray"
                  crop
                />
              </div>
            </div>
            {photos.length > 1 && (
              <button
                type="button"
                className="bg-subtle-gray br2 f2 fw-medium stone-700 absolute bottom-0 right-0 mb2 mb4-md mr2 mr4-md pv2 ph4 z-1"
                onClick={() => setOpenMediaCarousel(true)}
              >
                View all photos
              </button>
            )}
          </ClickableDiv>
          {openMediaCarousel && (
            <MediaGridCarousel
              mediaFiles={photos}
              onClose={() => setOpenMediaCarousel(false)}
            />
          )}
        </>
      )}
    </>
  );
}

PuppyParentPhotos.propTypes = {
  name: PropTypes.string.isRequired,
  photos: PropTypes.arrayOf(
    PropTypes.shape({
      url: PropTypes.string
    })
  )
};

PuppyParentPhotos.defaultProps = {
  photos: []
};

export default function PuppyParents() {
  const { puppy, litter } = usePuppyData();

  const parents = [litter.dad, litter.mom].filter(parent => !parent.tbd);

  if (parents.length === 0) {
    return null;
  }

  return (
    <SectionViewTracker
      name={ANALYTICS_PAGE_NAMES.PUPPY_PROFILE}
      category="puppy-profile-parents"
    >
      <Section className="PuppyParents bg-sand">
        <Section.Heading>Meet {puppy.name}’s parents</Section.Heading>
        <ul className="PuppyParents__list flex flex-column mt10 mt14-md">
          {parents.map(parent => {
            const badgeText = parent === litter.dad ? 'Dad' : 'Mom';
            const healthTesting = parent.health_testing;

            const subheading = [parent.breed, parent.age_description]
              .filter(Boolean)
              .join(' • ');

            return (
              <li key={parent.id}>
                <div className="row wider-gutters">
                  <div className="col-md-6 mb6 mb0-lg">
                    <PuppyParentPhotos
                      photos={parent.gallery}
                      name={parent.name}
                    />
                  </div>
                  <div className="col-md-6">
                    <div className="flex items-center">
                      <h4 className="heading-4">{parent.name}</h4>
                      <Badge className="branch-light bg-branch-dark ml4-md">
                        {badgeText}
                      </Badge>
                    </div>
                    <p className="secondary mt1 mt2-md mb4 mb6-md">
                      {subheading}
                    </p>
                    {parent.attributes.length > 0 && (
                      <ul className="PuppyParents__attributesList flex flex-wrap f2 mb8">
                        {parent.attributes.map((attr, i) => (
                          <li className="f2" key={i}>
                            <p className="fw-medium mb1">{attr.label}</p>
                            <p className="secondary">{attr.value}</p>
                          </li>
                        ))}
                      </ul>
                    )}
                    {parent.faq.map((q, i) => (
                      <div key={i} className="mt6 mt8-md">
                        <p className="fw-medium mb1">{q.question}</p>
                        <ClampedMultiLineText
                          fadeToBackgroundColor="#FDF9F5" // sand
                          className="secondary"
                          text={q.answer}
                          numOfLines={6}
                          lineHeight={21.44}
                          moreText="Read more"
                          lessText="Show less"
                          fadeToButton
                        />
                      </div>
                    ))}
                    {healthTesting && (
                      <ParentHealthTestingBanner
                        className="bg-white mt8"
                        healthTesting={healthTesting}
                        parentId={parent.id}
                      />
                    )}
                  </div>
                </div>
              </li>
            );
          })}
        </ul>
      </Section>
    </SectionViewTracker>
  );
}
